"use client";

import { DatePicker } from "@/components/ui/datepicker";
import { Button, Input, Select } from "rizzui";
import { PiMagnifyingGlassBold, PiPlusBold } from "react-icons/pi";
import { Link } from "react-router-dom";
import { routes } from "@/config/routes";
import { EPushNotificationStatus } from "@/utilities/types/PushNotification";

type DateRange = [Date | null, Date | null];

export default function FilterElement({
  onSearch,
  searchTerm,
  status,
  fromDate,
  toDate,
  updateFilter,
  handleReset,
}: {
  onSearch: (searchTerm: string) => void;
  searchTerm: string;
  status?: EPushNotificationStatus;
  fromDate?: string;
  toDate?: string;
  updateFilter: (key: string, value: any) => void;
  handleReset: () => void;
}) {
  function clearSearch() {
    onSearch("");
  }

  // DatePicker selectsRange stores Date in local state, we convert to ISO.
  const dateRange: DateRange = [
    fromDate ? new Date(fromDate) : null,
    toDate ? new Date(toDate) : null,
  ];

  function handleDateChange(range: DateRange) {
    const [start, end] = range;
    updateFilter("fromDate", start ? start.toISOString() : undefined);
    updateFilter("toDate", end ? end.toISOString() : undefined);
  }

  const hasActiveFilter = Boolean(status || fromDate || toDate);

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 lg:flex-nowrap">
      <div className="flex w-full flex-wrap items-center gap-3 md:w-auto">
        <Input
          type="search"
          placeholder="Search by title..."
          value={searchTerm}
          onClear={clearSearch}
          onChange={(e) => onSearch(e.target.value)}
          clearable
          prefix={<PiMagnifyingGlassBold className="h-4 w-4" />}
          className="w-full md:w-60"
        />
        <Select
          value={status ?? null}
          placeholder="All Statuses"
          options={[
            { value: EPushNotificationStatus.DRAFT, label: "Draft" },
            { value: EPushNotificationStatus.SCHEDULED, label: "Scheduled" },
            { value: EPushNotificationStatus.SENDING, label: "Sending" },
            { value: EPushNotificationStatus.SENT, label: "Sent" },
            { value: EPushNotificationStatus.FAILED, label: "Failed" },
            { value: EPushNotificationStatus.CANCELLED, label: "Cancelled" },
          ]}
          onChange={(option: any) =>
            updateFilter("status", option?.value ?? undefined)
          }
          onClear={() => updateFilter("status", undefined)}
          clearable
          className="w-full md:w-40"
        />
        <div className="w-full md:w-72">
          <DatePicker
            selectsRange
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            onChange={handleDateChange}
            dateFormat="yyyy-MM-dd"
            placeholderText="Created date range"
            inputProps={{
              clearable: hasActiveFilter,
              onClear: () => {
                updateFilter("fromDate", undefined);
                updateFilter("toDate", undefined);
              },
            }}
          />
        </div>
        {hasActiveFilter && (
          <Button
            variant="flat"
            size="sm"
            onClick={handleReset}
            className="text-gray-500"
          >
            Reset
          </Button>
        )}
      </div>
      <div className="flex w-full items-center gap-2 md:w-auto">
        <Link to={routes.pushNotification.create} className="w-full md:w-auto">
          <Button as="span" className="w-full md:w-auto">
            <PiPlusBold className="me-1.5 h-[17px] w-[17px]" />
            New Notification
          </Button>
        </Link>
      </div>
    </div>
  );
}
